import type { SavingInstitution, SavingPocket } from "@/hooks/useSavingPockets";

export type SavingPocketDisplayRow = SavingPocket & {
  kind: "pocket";
  institutionName: string;
};

export type UnifiedSavingPocketRow = {
  kind: "unified";
  id: string;
  name: string;
  institutionName: string;
  amount: number;
  anual_rate: number;
  monthly_return: number;
  yearly_return: number;
  freeze_time: number | null;
  description: string;
  count: number;
  sourceRows: SavingPocket[];
};

export type SavingPocketListRow = SavingPocketDisplayRow | UnifiedSavingPocketRow;

export function parseFreezeTime(freezeTime: string) {
  const days = Number(freezeTime);
  if (!Number.isFinite(days) || days <= 0) return null;
  return Math.trunc(days);
}

export function getUnifiedAnnualRate(rows: SavingPocket[]) {
  const total = rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
  if (total <= 0) return 0;
  return rows.reduce((sum, row) => sum + Number(row.amount ?? 0) * Number(row.anual_rate ?? 0), 0) / total;
}

export function resolveUnifiedFreezeTime(current: number | null, next: number | null) {
  if (!current) return next;
  if (!next) return current;
  return Math.min(current, next);
}

export function parseDateValue(value?: string | null) {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getUnfreezeDate(pocket: Pick<SavingPocket, "freeze_time" | "freeze_date" | "unfreeze_date">) {
  const explicitDate = parseDateValue(pocket.unfreeze_date);
  if (explicitDate) return explicitDate;
  if (!pocket.freeze_date || !pocket.freeze_time || !Number.isFinite(pocket.freeze_time) || pocket.freeze_time <= 0) return null;
  const date = parseDateValue(pocket.freeze_date);
  if (!date) return null;
  date.setDate(date.getDate() + pocket.freeze_time);
  return date;
}

export function getEarliestFreezeDate(pockets: SavingPocket[]) {
  return pockets
    .map((pocket) => parseDateValue(pocket.freeze_date))
    .filter((date): date is Date => Boolean(date))
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
}

export function getLatestUnfreezeDate(pockets: SavingPocket[]) {
  return pockets
    .map((pocket) => getUnfreezeDate(pocket))
    .filter((date): date is Date => Boolean(date))
    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;
}

export function getReturnAfterFreeze(row: SavingPocket) {
  if (row.freeze_time == null || row.freeze_time <= 0) return null;
  if (row.return_amount_after_freeze != null && Number.isFinite(Number(row.return_amount_after_freeze))) {
    return Number(row.return_amount_after_freeze);
  }
  return row.amount * Math.pow(1 + row.anual_rate / 100 / 360, row.freeze_time) - row.amount;
}

export const buildSavingPocketRows = (
  pockets: SavingPocket[],
  institutions: SavingInstitution[],
  fallbackName: string,
): SavingPocketDisplayRow[] => {
  const institutionNames = new Map(
    institutions.map((institution) => [institution.id, institution.name]),
  );
  return pockets.map((pocket) => ({
    ...pocket,
    kind: "pocket",
    institutionName: institutionNames.get(pocket.saving_institution_id) ?? fallbackName,
  }));
};

export const buildUnifiedSavingPocketRows = (
  rows: SavingPocketDisplayRow[],
): UnifiedSavingPocketRow[] => {
  const grouped = new Map<number, UnifiedSavingPocketRow>();

  rows.forEach((row) => {
    const current = grouped.get(row.saving_institution_id);
    if (current) {
      current.amount += Number(row.amount ?? 0);
      current.monthly_return += Number(row.monthly_return ?? 0);
      current.yearly_return += Number(row.yearly_return ?? 0);
      current.count += 1;
      current.freeze_time = resolveUnifiedFreezeTime(current.freeze_time, row.freeze_time ?? null);
      current.description ||= row.description ?? "";
      current.sourceRows.push(row);
      return;
    }

    grouped.set(row.saving_institution_id, {
      kind: "unified",
      id: `institution-${row.saving_institution_id}`,
      name: row.institutionName,
      institutionName: row.institutionName,
      amount: Number(row.amount ?? 0),
      anual_rate: 0,
      monthly_return: Number(row.monthly_return ?? 0),
      yearly_return: Number(row.yearly_return ?? 0),
      freeze_time: row.freeze_time ?? null,
      description: row.description ?? "",
      count: 1,
      sourceRows: [row],
    });
  });

  return [...grouped.values()]
    .map((row) => ({ ...row, anual_rate: getUnifiedAnnualRate(row.sourceRows) }))
    .sort((a, b) => b.amount - a.amount);
};

export const getSavingPocketOverview = (
  rows: SavingPocketDisplayRow[],
  institutions: SavingInstitution[],
) => {
  const institutionTotals = rows.reduce<Record<number, number>>((totals, row) => {
    totals[row.saving_institution_id] =
      (totals[row.saving_institution_id] ?? 0) + Number(row.amount ?? 0);
    return totals;
  }, {});
  const sortedInstitutions = [...institutions].sort(
    (a, b) => (institutionTotals[b.id] ?? 0) - (institutionTotals[a.id] ?? 0),
  );

  return {
    totalSaved: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    totalMonthlyReturn: rows.reduce(
      (sum, row) => sum + Number(row.monthly_return || 0),
      0,
    ),
    activeInstitutions: institutions.filter((institution) => institution.active).length,
    institutionTotals,
    activeInstitutionRows: sortedInstitutions.filter((institution) => institution.active),
    inactiveInstitutionRows: sortedInstitutions.filter((institution) => !institution.active),
    nextUnfreeze: rows
      .map((row) => getUnfreezeDate(row))
      .filter((value): value is Date => Boolean(value))
      .sort((a, b) => a.getTime() - b.getTime())[0] ?? null,
  };
};

export const splitSavingPocketRows = (rows: SavingPocketDisplayRow[]) =>
  rows.reduce(
    (result, row) => {
      const inactive =
        Number(row.amount ?? 0) <= 0 &&
        Number(row.monthly_return ?? 0) <= 0 &&
        Number(row.yearly_return ?? 0) <= 0 &&
        !row.freeze_time;
      result[inactive ? "inactivePocketRows" : "activePocketRows"].push(row);
      return result;
    },
    {
      activePocketRows: [] as SavingPocketDisplayRow[],
      inactivePocketRows: [] as SavingPocketDisplayRow[],
    },
  );
